import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router';
import { PageSkeleton, ErrorBoundary } from 'markcat-ui';

// Eager: Landing (first paint), Login, ResetPassword (common entry points),
// BillingSuccess/Cancel (Stripe redirects here — tiny, no reason to lazy-load).
import { Landing } from './pages/Landing';
import { Login } from './pages/Login';
import { ResetPassword } from './pages/ResetPassword';
import { BillingSuccess } from './pages/BillingSuccess';
import { BillingCancel } from './pages/BillingCancel';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { Imprint } from './pages/Imprint';
import { AppShell } from './routes/AppShell';
import { AuthGuard } from './routes/AuthGuard';
import { SessionBootstrap } from './routes/SessionBootstrap';
import { Share } from './pages/Share';
import { NotFound } from './pages/NotFound';
import { ConnectExtension } from './pages/ConnectExtension';

// Lazy: App views loaded on demand
const BookmarkManager = lazy(() => import('./pages/BookmarkManager').then(m => ({ default: m.BookmarkManager })));
const Processing = lazy(() => import('./pages/Processing').then(m => ({ default: m.Processing })));
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const Review = lazy(() => import('./pages/Review').then(m => ({ default: m.Review })));
const Billing = lazy(() => import('./pages/Billing').then(m => ({ default: m.Billing })));

function LazyBookmarks() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSkeleton variant="manager" />}>
        <BookmarkManager />
      </Suspense>
    </ErrorBoundary>
  );
}

function LazyProcessing() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSkeleton variant="processing" />}>
        <Processing />
      </Suspense>
    </ErrorBoundary>
  );
}

function LazySettings() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSkeleton variant="settings" />}>
        <Settings />
      </Suspense>
    </ErrorBoundary>
  );
}

function LazyReview() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSkeleton variant="manager" />}>
        <Review />
      </Suspense>
    </ErrorBoundary>
  );
}

function LazyBilling() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSkeleton variant="settings" />}>
        <Billing />
      </Suspense>
    </ErrorBoundary>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/app" element={<SessionBootstrap><AuthGuard><ErrorBoundary><AppShell /></ErrorBoundary></AuthGuard></SessionBootstrap>}>
        <Route index element={<LazyBookmarks />} />
        <Route path="share" element={<Share />} />
        <Route path="processing" element={<LazyProcessing />} />
        <Route path="review" element={<LazyReview />} />
        <Route path="settings" element={<LazySettings />} />
        <Route path="billing" element={<LazyBilling />} />
      </Route>
      <Route path="/login" element={<Login />} />
      <Route path="/connect-extension" element={<SessionBootstrap><AuthGuard><ConnectExtension /></AuthGuard></SessionBootstrap>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/billing/cancel" element={<BillingCancel />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/imprint" element={<Imprint />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
